import { useRef } from "react";
import { Alert, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import {
  getCompetitorId,
  getCompetitorLabel,
  getStageLabel,
  getStageNumber,
} from "../utils/ranking.utils";

const RankingForm = ({
  form,
  validationErrors = {},
  submitting = false,
  readOnly = false,
  categories = [],
  isCategoriesLoading = false,
  stages = [],
  isStagesLoading = false,
  competitors = [],
  isCompetitorsLoading = false,
  rallyName = "",
  importMessage = "",
  importError = "",
  submitLabel = "Save rankings",
  submittingLabel = "Saving...",
  onSubmit,
  onFieldChange,
  onRowChange,
  onAddRow,
  onRemoveRow,
  onDownloadSample,
  onExport,
  onImportFile,
  onBack,
}) => {
  const fileInputRef = useRef(null);
  const disabled = submitting || readOnly;
  const rankings = form.rankings || [];
  const usedTeamIds = new Set(
    rankings.map((row) => String(row.team_id || "").trim()).filter(Boolean)
  );

  const handleImportClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onImportFile?.(file);
  };

  return (
    <Form onSubmit={onSubmit}>
      {rallyName ? (
        <p className="text-muted mb-4">
          Setting rankings for <strong>{rallyName}</strong>. This replaces the
          full snapshot for the selected category and stage. Download the
          sample, fill it in Excel/CSV, import it here, edit if needed, then
          submit.
        </p>
      ) : null}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Category <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={form.category}
              onChange={(e) => onFieldChange("category", e.target.value)}
              isInvalid={!!validationErrors.category}
              disabled={disabled || isCategoriesLoading}
            >
              <option value="">
                {isCategoriesLoading
                  ? "Loading categories..."
                  : "Select a category"}
              </option>
              {categories.map((category) => {
                const key = category.key || category._id || category.id;
                return (
                  <option key={key} value={category.key || key}>
                    {category.title || category.key || "Untitled category"}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.category}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              After stage <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={form.after_stage}
              onChange={(e) => onFieldChange("after_stage", e.target.value)}
              isInvalid={!!validationErrors.after_stage}
              disabled={disabled || isStagesLoading}
            >
              <option value="">
                {isStagesLoading ? "Loading stages..." : "Select a stage"}
              </option>
              {stages.map((stage, index) => {
                const number = getStageNumber(stage);
                const value = number === 0 || number ? number : index + 1;
                return (
                  <option
                    key={stage._id || stage.id || value}
                    value={value}
                  >
                    {getStageLabel(stage)}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.after_stage}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h5 className="mb-0">Rankings table</h5>
        <div className="d-flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={onDownloadSample}
            disabled={disabled}
          >
            Download sample
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={handleImportClick}
            disabled={disabled}
          >
            Import Excel/CSV
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={onExport}
            disabled={disabled || !rankings.length}
          >
            Export
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={onAddRow}
            disabled={disabled}
          >
            + Add row
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="d-none"
            onChange={handleFileChange}
          />
        </div>
      </div>

      {importMessage ? (
        <Alert variant="success" className="mb-3">
          {importMessage}
        </Alert>
      ) : null}
      {importError ? (
        <Alert variant="danger" className="mb-3">
          {importError}
        </Alert>
      ) : null}

      {validationErrors.rankings ? (
        <p className="text-danger small">{validationErrors.rankings}</p>
      ) : null}

      <div className="table-responsive border rounded">
        <Table className="mb-0 align-middle" bordered hover>
          <thead className="table-light">
            <tr>
              <th style={{ width: 56 }}>#</th>
              <th style={{ minWidth: 220 }}>
                Team <span className="text-danger">*</span>
              </th>
              <th style={{ width: 110 }}>
                Position <span className="text-danger">*</span>
              </th>
              <th style={{ minWidth: 140 }}>Total time</th>
              <th style={{ minWidth: 140 }}>Total penalty</th>
              <th style={{ width: 110 }}>Points</th>
              <th style={{ width: 90 }} className="text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rankings.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  No ranking rows yet. Import a file or add a row.
                </td>
              </tr>
            ) : (
              rankings.map((row, index) => {
                const rowError = validationErrors.rows?.[index] || {};
                return (
                  <tr key={`ranking-row-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.team_id}
                        onChange={(e) =>
                          onRowChange(index, { team_id: e.target.value })
                        }
                        isInvalid={!!rowError.team_id}
                        disabled={
                          disabled ||
                          isCompetitorsLoading ||
                          !form.category
                        }
                      >
                        <option value="">
                          {!form.category
                            ? "Select a category first"
                            : isCompetitorsLoading
                              ? "Loading teams..."
                              : "Select a team"}
                        </option>
                        {competitors.map((competitor) => {
                          const id = getCompetitorId(competitor);
                          const selectedElsewhere =
                            usedTeamIds.has(id) && row.team_id !== id;
                          return (
                            <option
                              key={id}
                              value={id}
                              disabled={selectedElsewhere}
                            >
                              {getCompetitorLabel(competitor)}
                            </option>
                          );
                        })}
                      </Form.Select>
                      {rowError.team_id ? (
                        <div className="invalid-feedback d-block">
                          {rowError.team_id}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="1"
                        step="1"
                        value={row.position}
                        onChange={(e) =>
                          onRowChange(index, { position: e.target.value })
                        }
                        isInvalid={!!rowError.position}
                        disabled={disabled}
                      />
                      {rowError.position ? (
                        <div className="invalid-feedback d-block">
                          {rowError.position}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.total_time}
                        onChange={(e) =>
                          onRowChange(index, {
                            total_time: e.target.value,
                          })
                        }
                        placeholder="49H 00' 41'"
                        disabled={disabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.total_penalty}
                        onChange={(e) =>
                          onRowChange(index, {
                            total_penalty: e.target.value,
                          })
                        }
                        placeholder="00H 00' 00'"
                        disabled={disabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={row.points}
                        onChange={(e) =>
                          onRowChange(index, { points: e.target.value })
                        }
                        isInvalid={!!rowError.points}
                        disabled={disabled}
                      />
                      {rowError.points ? (
                        <div className="invalid-feedback d-block">
                          {rowError.points}
                        </div>
                      ) : null}
                    </td>
                    <td className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => onRemoveRow(index)}
                        disabled={disabled || rankings.length <= 1}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={disabled}>
          {submitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                className="me-2"
              />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RankingForm;
